import { defineComponent, PropType } from '@vue/composition-api';

import { FieldErrors } from '@/models';
import {
  ComputationNodeSubtype,
  ExpressionNodeTree,
  IAbstractComputationNode,
  IAbstractComputationNodeData,
  convertExpressionTreeToNodeList,
  convertNodeListToExpressionTree
} from '@/models/computation';

import { ComputationExpressionTree } from './ComputationExpressionTree.vue';

class ComputationExpressionTreeContainerFactory<
  T extends IAbstractComputationNode,
  K = IAbstractComputationNodeData
> {
  define() {
    return defineComponent({
      name: 'ComputationTreeView',

      props: {
        value: {
          required: false,
          type: Array as PropType<T[]>,
          default: () => []
        },

        fieldErrors: {
          required: false,
          type: Array as PropType<FieldErrors[]>,
          default: () => []
        },

        maxTreeDepthLevel: {
          required: false,
          type: Number
        },

        nodeClass: {
          required: true,
          type: Function as PropType<{
            new (args: K): T;
          }>
        },

        disabled: {
          type: Boolean,
          default: false
        },

        disableBinaryExpression: {
          type: Boolean,
          default: false
        },

        disableUnaryExpression: {
          type: Boolean,
          default: false
        },

        defaultOperandSubtype: {
          type: Number as PropType<ComputationNodeSubtype>,
          default: ComputationNodeSubtype.Constant
        }
      },

      data() {
        return {
          internalLazyValue: this.value
        };
      },

      computed: {
        internalValue: {
          get(): ExpressionNodeTree<T> {
            return convertNodeListToExpressionTree(this.internalLazyValue);
          },
          set(val: ExpressionNodeTree<T>) {
            const newValue = convertExpressionTreeToNodeList(val);
            // update node list indices
            newValue.map((node, index) => {
              node.order = index + 1;
            });

            if (newValue === this.internalLazyValue) return;

            this.internalLazyValue = newValue;

            // hack for passing multiple function on the emit values
            this.$emit('input', newValue);
          }
        }
      },

      watch: {
        value: {
          handler(val) {
            this.internalLazyValue = val;
          },
          deep: true
        }
      },

      render(h) {
        return h(
          'v-container',
          {
            class: 'pa-0',
            attrs: {
              fluid: true
            }
          },
          [
            h(
              'div',
              {
                class: 'v-treeview theme--light position-relative'
              },
              [
                h(ComputationExpressionTree<T>(), {
                  props: {
                    value: this.internalValue,
                    disabled: this.disabled,
                    fieldErrors: this.fieldErrors,
                    nodeClass: this.nodeClass,
                    disableBinaryExpression: this.disableBinaryExpression,
                    disableUnaryExpression: this.disableUnaryExpression,
                    defaultOperandSubtype: this.defaultOperandSubtype
                  },
                  on: {
                    input: (nodes: T[]) => {
                      this.internalValue = nodes;
                    }
                  },
                  scopedSlots: {
                    'operator-node': value => {
                      return this.$scopedSlots['operator-node']!(value);
                    },
                    'operand-node': value => {
                      return this.$scopedSlots['operand-node']!(value);
                    }
                  }
                })
              ]
            )
          ]
        );
      }
    });
  }
}

// https://awad.dev/blog/generically-typed-vue-components/
const main = new ComputationExpressionTreeContainerFactory().define();

export function ComputationExpressionTreeContainer<
  T extends IAbstractComputationNode,
  K extends IAbstractComputationNodeData
>() {
  return main as ReturnType<
    ComputationExpressionTreeContainerFactory<T, K>['define']
  >;
}
export default main;
